import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  IconButton,
  Snackbar,
  SnackbarCloseReason,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import {
  APP_VERSION_MISMATCH_EVENT,
  hasVersionMismatch,
} from "../services/apiClient";

/**
 * Listens for `APP_VERSION_MISMATCH_EVENT` (dispatched by apiClient when the
 * API's X-App-Version response header has a different major.minor than the
 * bundle's own REACT_APP_SENTRY_RELEASE) and shows a persistent, non-blocking
 * toast prompting the user to reload. Since the event fires at most once per
 * page load, dismissing the toast will not bring it back.
 */
const VersionSkewToast: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // The event fires at most once per page load, so a dispatch that happened
    // before this listener attached would otherwise be lost — catch up first.
    if (hasVersionMismatch()) {
      setOpen(true);
    }
    const handleVersionMismatch = () => setOpen(true);
    window.addEventListener(
      APP_VERSION_MISMATCH_EVENT,
      handleVersionMismatch
    );
    return () => {
      window.removeEventListener(
        APP_VERSION_MISMATCH_EVENT,
        handleVersionMismatch
      );
    };
  }, []);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ zIndex: 1500 }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ width: "100%" }}
        action={
          <>
            <Button color="inherit" size="small" onClick={handleReload}>
              Reload
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        A new version of the app is available.
      </Alert>
    </Snackbar>
  );
};

export default VersionSkewToast;
